import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import API_URL from "../config";
import { getApiErrorMessage, getAuthHeaders, getStoredAccessToken, handleExpiredSession } from "../utils/authSession";
import { addLocalDevUser, getLocalDevUsers, isLocalDevCompanyId } from "../utils/localDevAuth";

const CreateUser = ({ user, returnPath = "/admin" }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "team_member",
    designation: "",
    mobile_number: "",
  });

  const [managers, setManagers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);
  const [error, setError] = useState("");

  const companyId = user?.company?.id;
  const isLocalDevSession = isLocalDevCompanyId(companyId) || isLocalDevCompanyId(localStorage.getItem('company_id'));

  /* ================= FETCH MANAGERS ================= */
  useEffect(() => {
    if (!companyId) return;

    const fetchManagers = async () => {
      if (isLocalDevSession) {
        const companyManagers = getLocalDevUsers(companyId).filter((u) => u.role === "manager");
        setManagers(companyManagers);
        if (formData.role === "team_member") {
          setManagerId(companyManagers[0]?.id?.toString() || "");
        }
        return;
      }

      try {
        const token = getStoredAccessToken();
        if (!token) {
          handleExpiredSession({ navigate, companyId });
          return;
        }

        setIsLoadingManagers(true);
        setError("");

        const res = await axios.get(`${API_URL}/api/users/`, {
          headers: getAuthHeaders(token),
        });

        const companyManagers = res.data.filter(
          (u) =>
            u.role === "manager" &&
            String(u.company?.id) === String(companyId)
        );

        setManagers(companyManagers);

        if (formData.role === "team_member") {
          setManagerId(companyManagers[0]?.id?.toString() || "");
        }
      } catch (err) {
        console.error(err);
        if (err.response?.status === 401) {
          handleExpiredSession({ navigate, companyId });
          return;
        }
        setError(getApiErrorMessage(err, {
          fallback: "Failed to load managers.",
        }));
      } finally {
        setIsLoadingManagers(false);
      }
    };

    fetchManagers();
  }, [companyId, formData.role, navigate, isLocalDevSession]);

  /* ================= ROLE CHANGE ================= */
  useEffect(() => {
    if (formData.role === "manager") {
      setManagerId("");
    } else if (managers.length > 0) {
      setManagerId(managers[0].id.toString());
    }
  }, [formData.role, managers]);

  /* ================= INPUT HANDLER ================= */
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "manager_id") {
      setManagerId(value);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= SUBMIT ================= */
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError("");

    try {
      if (isLocalDevSession) {
        addLocalDevUser({
          ...formData,
          companyId,
          managerId,
        });
        alert("User created successfully!");
        navigate(`${returnPath}?refresh=true`);
        return;
      }

      const token = getStoredAccessToken();
      if (!token) {
        handleExpiredSession({ navigate, companyId });
        return;
      }

      // REQUIRED FIELDS CHECK
      if (!formData.email || !formData.password || !formData.role) {
        setError("Please fill all required fields.");
        return;
      }

      // Team member must have manager
      if (formData.role === "team_member" && !managerId) {
        setError("Please select a manager for the team member.");
        return;
      }
      if (!formData.designation || !formData.mobile_number) {
        setError("Please fill all fields.");
        return;
      }

      if (!formData.email || !formData.password) {
        throw new Error("Email and password are required.");
      }

      if (formData.role === "team_member" && !managerId) {
        throw new Error("Please assign a manager.");
      }

      const payload = {
        email: formData.email,
        password: formData.password,
        role: formData.role,
        designation: formData.designation || null,
        mobile_number: formData.mobile_number || null,
        company_id: companyId,
        manager: formData.role === "team_member" ? managerId : null,
      };

      const userRes = await axios.post(`${API_URL}/api/users/`, payload, {
        headers: getAuthHeaders(token),
      });

      if (formData.role === "team_member") {
        await axios.post(
          `${API_URL}/api/team-members/`,
          {
            user_id: userRes.data.id,
            manager_id: managerId,
            performance_score: 0,
          },
          { headers: getAuthHeaders(token) }
        );
      }

      alert("User created successfully!");
      navigate(`${returnPath}?refresh=true`);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        handleExpiredSession({ navigate, companyId });
        return;
      }
      setError(getApiErrorMessage(err, {
        fallback: "Failed to create user",
      }));
    }
  };

  const isSubmitDisabled =
    isLoadingManagers ||
    (formData.role === "team_member" && !managerId);

  /* ================= UI ================= */
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 overflow-x-hidden">
      <div className="card w-full max-w-[380px] p-8">
        <h2 className="section-title mb-5 text-center text-xl">
          Create New User
        </h2>

        {error && (
          <div className="alert-error mb-4 text-center">{error}</div>
        )}

        <form onSubmit={handleCreateUser} className="space-y-3">
          <input
            type="email"
            name="email"
            placeholder="Email"
            className="input-field w-full px-3 py-2"
            value={formData.email}
            onChange={handleInputChange}
            required
          />

          <input
            type="password"
            name="password"
            placeholder="Password"
            className="input-field w-full px-3 py-2"
            value={formData.password}
            onChange={handleInputChange}
            required
          />

          <select
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            className="input-field w-full px-3 py-2"
          >
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="team_member">Team Member</option>
          </select>

          {/* DESIGNATION */}
          <input
            type="text"
            name="designation"
            placeholder="Designation"
            className="input-field w-full px-3 py-2"
            value={formData.designation}
            onChange={handleInputChange}
          />

          {/* MOBILE NUMBER */}
          <input
            type="text"
            name="mobile_number"
            placeholder="Mobile number"
            className="input-field w-full px-3 py-2"
            value={formData.mobile_number}
            onChange={handleInputChange}
          />

          {formData.role === "team_member" && (
            <select
              name="manager_id"
              value={managerId}
              onChange={handleInputChange}
              className="input-field w-full px-3 py-2"
              required
            >
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.email}
                </option>
              ))}
            </select>
          )}

          <button
            type="submit"
            disabled={isSubmitDisabled}
            className="primary-btn w-full py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create User
          </button>

          <Link
            to={returnPath}
            className="block text-center text-sm text-blue-600 hover:text-hover"
          >
            Cancel
          </Link>
        </form>
      </div>
    </div>
  );
};

export default CreateUser;
